/**
 * Géolocalisation à l'arrivée sur « Nos revendeurs » (chantier
 * store-locator-revendeurs, point 5, critères 1-2, scénarios 1-8, 12a, 54).
 *
 * Module volontairement séparé de MapboxCluster.js (DOM/carte) : uniquement
 * deux fonctions appelées par lui, pour rester lisible et pour que la partie
 * réseau (géocodage inverse) soit interceptable par l'aide de test
 * `tests/e2e/helpers/mapbox-fictif.ts`, jamais un vrai appel au service
 * pendant les essais automatiques.
 *
 * Portée volontairement restreinte à la page « Nos revendeurs » : ce module
 * n'est importé et appelé que depuis MapboxCluster.js, dans la branche
 * `!is_basic` de `mounted()` — jamais sur les pages « ville »
 * (`template-lp.php`) ni sur une page d'accueil qui reprendrait la même
 * carte en mode `is_basic` (invariant R8, scénario 54) : ces pages ne
 * montent jamais le geocoder, donc n'appellent jamais ce module.
 */

/**
 * Délai maximal avant repli silencieux (critère 1 : « délai dépassé »).
 * Valeur modeste : une géolocalisation qui met plus longtemps qu'un aller
 * simple au service de carte ne doit pas laisser le visiteur dans
 * l'incertitude — la page reste de toute façon utilisable pendant l'attente
 * (aucun blocage de l'affichage, la liste et la carte sont déjà rendues).
 */
export const GEOLOCATION_TIMEOUT_MS = 8000;

/**
 * Demande la position au visiteur si l'API est disponible et répond, sinon
 * replie immédiatement et silencieusement. Couvre en un seul chemin
 * `onFallback` : refus, blocage par le navigateur, API absente
 * (`navigator.geolocation` indéfini), contexte non sécurisé (le navigateur
 * invoque alors lui-même le callback d'erreur, avant toute simulation côté
 * test) et délai dépassé — jamais d'exception remontée, jamais d'erreur
 * visible au visiteur (critères 1-2, scénarios 2, 3, 4, 12a).
 * @param {object} handlers
 * @param {(point: {lng: number, lat: number}) => void} handlers.onSuccess
 * @param {() => void} handlers.onFallback
 */
export function requestGeolocation({ onSuccess, onFallback }) {
  if (
    typeof navigator === 'undefined'
    || !navigator.geolocation
    || typeof navigator.geolocation.getCurrentPosition !== 'function'
  ) {
    onFallback();
    return;
  }

  try {
    navigator.geolocation.getCurrentPosition(
      (position) => {
        onSuccess({ lng: position.coords.longitude, lat: position.coords.latitude });
      },
      () => {
        // Refusée, bloquée, indisponible ou contexte non sécurisé : le
        // navigateur retourne un objet d'erreur (PERMISSION_DENIED,
        // POSITION_UNAVAILABLE ou TIMEOUT) ; aucun n'est distingué ici, les
        // trois replient de la même façon (critère 2).
        onFallback();
      },
      { timeout: GEOLOCATION_TIMEOUT_MS, maximumAge: 0 },
    );
  } catch (error) {
    // Certains navigateurs/extensions peuvent lever une exception
    // synchrone plutôt que d'appeler le callback d'erreur (ex. API
    // désactivée par une politique) : même repli silencieux.
    onFallback();
  }
}

/**
 * Géocodage inverse Mapbox (position → adresse), pour écrire l'adresse
 * reconnue dans la barre de recherche (critère 1). Position brute envoyée
 * uniquement à ce service, jamais mémorisée ni transmise ailleurs (voir
 * StoreReferencePoint.js pour ce qui EST mémorisé : le point arrondi, dans
 * `sessionStorage` seulement). Ne lève jamais : un échec réseau ou une
 * réponse vide renvoie `null`, sans erreur visible — la carte reste centrée
 * et la liste triée même sans texte d'adresse trouvé.
 * @param {{lng: number, lat: number, accessToken: string}} params
 * @returns {Promise<string|null>}
 */
export async function reverseGeocodeAddress({ lng, lat, accessToken }) {
  if (!accessToken) {
    return null;
  }
  try {
    const url = `https://api.mapbox.com/geocoding/v5/mapbox.places/${lng},${lat}.json`
      + `?types=address&language=fr&limit=1&access_token=${encodeURIComponent(accessToken)}`;
    const response = await fetch(url);
    if (!response.ok) {
      return null;
    }
    const body = await response.json();
    const feature = Array.isArray(body?.features) ? body.features[0] : null;
    return typeof feature?.place_name === 'string' ? feature.place_name : null;
  } catch (error) {
    return null;
  }
}
